// src/pages/SystemSetupPage.js
import React, { useState } from 'react';
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import { Helmet } from 'react-helmet';
import '../styles/deviceListPage.css';
import '../styles/systemSetupPage.css';
import { GET_SYSTEM } from '../graphql/queries/getSystem';
import { LIST_ORGANISATIONS } from '../graphql/queries/listOrganisations';
import { 
  GET_SYSTEM_TIMELINE, 
  GET_ALERTS_PAGE, 
  GET_ALERT_REPORTS_PAGE, 
  GET_SHUTDOWN_PAGE, 
  GET_BOOT_PAGE,
  GET_NETWORK_PAGE, 
  GET_CONNECTIVITY_PAGE, 
  GET_BATTERY_PAGE, 
  GET_PENDANT_PAGE, 
  GET_PAIRING_PAGE 
} from '../graphql/queries/getSystemTimeline';
import { renderLocalTimeWithUtcTooltip } from '../utils/dateTimeHelpers';
import { SEND_COMMAND } from '../graphql/mutations/sendCommand';
import { SET_PERS_ID } from '../graphql/mutations/setPersId';
import { SET_ALERT_ENDPOINTS } from '../graphql/mutations/setAlertEndpoints';
import { CHANGE_SYSTEM_STATE } from '../graphql/mutations/changeSystemState';
import { ASSIGN_NICKNAME } from '../graphql/mutations/assignNickname';
import { ASSIGN_CUSTOMER_REFERENCE } from '../graphql/mutations/assignCustomerReference';
import { SET_FEATURES } from '../graphql/mutations/setFeatures';
import { ASSIGN_ORGANISATION } from '../graphql/mutations/assignOrganisation';

const ORGANISATION_PAGE_SIZE = 100;
const TUNSTALL_PROVIDER = 'Tunstall';
// Hidden until the backend can change a system's PERS region.
const SHOW_TUNSTALL_PERS_REGION = false;

const organisationFieldScore = (org, current) => {
  if (!current) return 0;
  return ['village_operator', 'village_name', 'care_provider', 'care_provider_region']
    .reduce((score, field) => {
      const left = (org?.[field] || '').trim();
      const right = (current?.[field] || '').trim();
      return score + (left === right ? 1 : 0);
    }, 0);
};

const tunstallRegionsFromOrganisations = (items, currentSetting) => {
  const byRegion = new Map();
  (items || []).forEach((org) => {
    if ((org?.pers_provider || '').trim() !== TUNSTALL_PROVIDER) return;
    const region = (org?.pers_region || '').trim();
    if (!region) return;
    const candidate = { ...org, pers_provider: TUNSTALL_PROVIDER, pers_region: region };
    const existing = byRegion.get(region);
    if (!existing || organisationFieldScore(candidate, currentSetting) > organisationFieldScore(existing, currentSetting)) {
      byRegion.set(region, candidate);
    }
  });
  return Array.from(byRegion.values()).sort((a, b) => a.pers_region.localeCompare(b.pers_region));
};

const toOrganisationSettingInput = (org) => ({
  pers_provider: org.pers_provider ?? null,
  pers_region: org.pers_region ?? null,
  village_operator: org.village_operator || null,
  village_name: org.village_name || null,
  care_provider: org.care_provider || null,
  care_provider_region: org.care_provider_region || null,
});

const SystemSetupPage = () => {
  const PAGE_DISABLED = false; 
  const [systemId, setSystemId] = useState('');
  const [systemData, setSystemData] = useState(null);
  const [rawApiResponse, setRawApiResponse] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('');
  
  // State for editable fields
  const [nickname, setNickname] = useState('');
  const [customerReference, setCustomerReference] = useState('');
  const [persId, setPersId] = useState('');
  const [systemStatus, setSystemStatus] = useState('INACTIVE');
  const [alertEndpoints, setAlertEndpoints] = useState(['', '', '', '']);
  const [smokeAlarmDetection, setSmokeAlarmDetection] = useState(false);
  const [persRegion, setPersRegion] = useState('');
  const [tunstallRegions, setTunstallRegions] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [regionsError, setRegionsError] = useState('');

  const client = useApolloClient();
  const [fetchSystem, { loading, error, data: apolloData }] = useLazyQuery(GET_SYSTEM, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all' // This allows partial data to be returned even with errors
  });

  const [sendCommandMutation, { loading: sendingCommand }] = useMutation(SEND_COMMAND);
  const [setPersIdMutation, { loading: savingPersId }] = useMutation(SET_PERS_ID);
  const [setAlertEndpointsMutation, { loading: savingEndpoints }] = useMutation(SET_ALERT_ENDPOINTS);
  const [changeSystemStateMutation, { loading: savingSystemState }] = useMutation(CHANGE_SYSTEM_STATE);
  const [assignNicknameMutation, { loading: savingNickname }] = useMutation(ASSIGN_NICKNAME);
  const [assignCustomerReferenceMutation, { loading: savingCustomerReference }] = useMutation(ASSIGN_CUSTOMER_REFERENCE);
  const [setFeaturesMutation] = useMutation(SET_FEATURES);
  const [assignOrganisationMutation] = useMutation(ASSIGN_ORGANISATION);
  const [isSaving, setIsSaving] = useState(false);
  const [saveErrorMessage, setSaveErrorMessage] = useState('');

  // Timeline state
  const [timelineData, setTimelineData] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineLoadingStatus, setTimelineLoadingStatus] = useState('');

  // Timeline lazy queries
  const [fetchTimeline] = useLazyQuery(GET_SYSTEM_TIMELINE, { fetchPolicy: 'network-only', errorPolicy: 'all' });
  const [fetchAlertsPage] = useLazyQuery(GET_ALERTS_PAGE, { fetchPolicy: 'network-only', errorPolicy: 'all' });
  const [fetchAlertReportsPage] = useLazyQuery(GET_ALERT_REPORTS_PAGE, { fetchPolicy: 'network-only', errorPolicy: 'all' });
  const [fetchShutdownPage] = useLazyQuery(GET_SHUTDOWN_PAGE, { fetchPolicy: 'network-only', errorPolicy: 'all' });
  const [fetchBootPage] = useLazyQuery(GET_BOOT_PAGE, { fetchPolicy: 'network-only', errorPolicy: 'all' });
  const [fetchNetworkPage] = useLazyQuery(GET_NETWORK_PAGE, { fetchPolicy: 'network-only', errorPolicy: 'all' });
  const [fetchConnectivityPage] = useLazyQuery(GET_CONNECTIVITY_PAGE, { fetchPolicy: 'network-only', errorPolicy: 'all' });
  const [fetchBatteryPage] = useLazyQuery(GET_BATTERY_PAGE, { fetchPolicy: 'network-only', errorPolicy: 'all' });
  const [fetchPendantPage] = useLazyQuery(GET_PENDANT_PAGE, { fetchPolicy: 'network-only', errorPolicy: 'all' });
  const [fetchPairingPage] = useLazyQuery(GET_PAIRING_PAGE, { fetchPolicy: 'network-only', errorPolicy: 'all' });

  // Early return while page is disabled
  if (PAGE_DISABLED) {
    return (
      <div className="device-list-page">
        <Helmet>
          <title>System Setup (Temporarily Disabled)</title>
        </Helmet>
        <div className="system-setup-container">
          <div className="system-info-section">
            <h4>System Setup</h4>
            <p>This page is temporarily disabled and will be re-enabled in a future update.</p>
          </div>
        </div>
      </div>
    );
  }

  // Force Unpair countdown state
  const [showUnpairCountdown, setShowUnpairCountdown] = useState(false);
  const [unpairCountdownKey, setUnpairCountdownKey] = useState(0);

  // Helper function to update all fields with system data
  const updateFieldsWithSystemData = (systemDataToUse) => {
    setSystemData(systemDataToUse);
    setRawApiResponse({ data: { getSystem: systemDataToUse } });
    const loadTime = new Date().toLocaleString();
    setLoadingMessage(`System data loaded successfully at ${loadTime}`);
    
    // Update editable fields with API data
    setNickname(systemDataToUse.basic_info?.nickname || '');
    setCustomerReference(systemDataToUse.basic_info?.customer_reference || '');
    setPersId(systemDataToUse.configuration?.pers_id || '');
    setSystemStatus(systemDataToUse.basic_info?.system_status?.system_state || 'INACTIVE');
    
    // Handle alert endpoints - always show 4 boxes
    const endpoints = ['', '', '', ''];
    if (systemDataToUse.configuration?.alert_endpoints) {
      systemDataToUse.configuration.alert_endpoints.slice(0, 4).forEach((endpoint, index) => {
        endpoints[index] = endpoint.address || '';
      });
    }
    setAlertEndpoints(endpoints);
    setSmokeAlarmDetection(Boolean(systemDataToUse.configuration?.features?.smoke_alarm_detection));
    setPersRegion((systemDataToUse.advanced_info?.organisation_setting?.pers_region || '').trim());
  };

  // Effect to handle Apollo data changes (both success and partial error cases)
  React.useEffect(() => {
    if (apolloData && apolloData.getSystem) {
      console.log('Apollo data updated:', apolloData);
      updateFieldsWithSystemData(apolloData.getSystem);
    }
  }, [apolloData]);

  // Effect to handle loading completion and errors
  React.useEffect(() => {
    if (!loading) {
      if (error && !apolloData?.getSystem) {
        // Only show error if we truly have no data
        console.error('GraphQL error received:', error);
        if (error.message.includes('Not Authorized') || error.message.includes('Item not found')) {
          setLoadingMessage('System data loaded successfully');
        } else {
          setLoadingMessage('Error loading system data. Please try again.');
        }
      } else if (!apolloData?.getSystem && !systemData) {
        setLoadingMessage('No system found with that ID');
      }
    }
  }, [loading, error, apolloData, systemData]);

  React.useEffect(() => {
    const setting = systemData?.advanced_info?.organisation_setting;
    const provider = (setting?.pers_provider || '').trim();
    if (!SHOW_TUNSTALL_PERS_REGION || provider !== TUNSTALL_PROVIDER) {
      setTunstallRegions((current) => (current.length === 0 ? current : []));
      setRegionsError((current) => (current ? '' : current));
      setRegionsLoading((current) => (current ? false : current));
      return undefined;
    }

    let cancelled = false;
    const loadTunstallRegions = async () => {
      setRegionsLoading(true);
      setRegionsError('');
      const items = [];
      let nextToken = null;
      const seenTokens = new Set();

      try {
        do {
          if (nextToken) {
            if (seenTokens.has(nextToken)) break;
            seenTokens.add(nextToken);
          }

          const result = await client.query({
            query: LIST_ORGANISATIONS,
            variables: nextToken
              ? { limit: ORGANISATION_PAGE_SIZE, nextToken }
              : { limit: ORGANISATION_PAGE_SIZE },
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
          });

          const page = result?.data?.listOrganisations;
          if (!page) {
            const message = result?.errors?.map((err) => err.message).filter(Boolean).join('; ');
            throw result?.error || new Error(message || 'Organisation list was empty');
          }

          if (Array.isArray(page.items)) {
            items.push(...page.items);
          }
          nextToken = page.nextToken || null;
        } while (nextToken);

        if (!cancelled) {
          setTunstallRegions(tunstallRegionsFromOrganisations(items, setting));
        }
      } catch (e) {
        console.error('Failed to load organisations', e);
        if (!cancelled) {
          setTunstallRegions([]);
          setRegionsError('Could not load Tunstall PERS regions.');
        }
      } finally {
        if (!cancelled) setRegionsLoading(false);
      }
    };

    loadTunstallRegions();
    return () => {
      cancelled = true;
    };
  }, [client, systemData?.system_id, systemData?.advanced_info?.organisation_setting?.pers_provider]);

  const handleSystemIdChange = (e) => {
    setSystemId(e.target.value);
  };

  const handleLoadSystem = () => {
    if (!systemId.trim()) {
      setLoadingMessage('Please enter a system ID');
      return;
    }
    
    setLoadingMessage('Loading system data...');
    setSystemData(null);
    setRawApiResponse(null);
    
    // Reset editable fields
    setNickname('');
    setCustomerReference('');
    setPersId('');
    setSystemStatus('INACTIVE');
    setAlertEndpoints(['', '', '', '']);
    setSmokeAlarmDetection(false);
    setPersRegion('');
    setTunstallRegions([]);
    setRegionsError('');
    
    // Reset timeline state
    setTimelineData([]);
    setTimelineLoadingStatus('');
    
    // Use Apollo's useLazyQuery which was working before
    fetchSystem({
      variables: {
        system_id: systemId.trim()
      }
    });
  };


  const renderSystemData = () => {
    if (!systemData) return null;

    // Helper function to safely check if an object exists and has items
    const hasItems = (obj, path) => {
      try {
        const parts = path.split('.');
        let current = obj;
        for (const part of parts) {
          if (current === null || current === undefined) return false;
          current = current[part];
        }
        return Array.isArray(current) && current.length > 0;
      } catch (e) {
        return false;
      }
    };

    // Helper function to safely get a value from a potentially undefined nested path
    const safeGet = (obj, path, defaultValue = 'N/A') => {
      try {
        const parts = path.split('.');
        let current = obj;
        for (const part of parts) {
          if (current === null || current === undefined) return defaultValue;
          current = current[part];
        }
        return current !== null && current !== undefined ? current : defaultValue;
      } catch (e) {
        return defaultValue;
      }
    };

    const currentSystemState = safeGet(systemData, 'basic_info.system_status.system_state', 'N/A');
    const isForceUnpairDisabled = currentSystemState !== 'INACTIVE';
    const persProvider = (systemData.advanced_info?.organisation_setting?.pers_provider || '').trim();
    const isTunstallProvider = persProvider === TUNSTALL_PROVIDER;
    const savedPersRegion = (systemData.advanced_info?.organisation_setting?.pers_region || '').trim();
    const regionChoices = Array.from(new Set([
      ...tunstallRegions.map((org) => org.pers_region),
      ...(savedPersRegion ? [savedPersRegion] : []),
    ])).sort((a, b) => a.localeCompare(b));

    const renderPersRegionSelect = (id) => (
      <select
        id={id}
        className="form-control pers-region-select"
        aria-label="PERS Region"
        value={regionChoices.includes(persRegion) ? persRegion : ''}
        onChange={(e) => setPersRegion(e.target.value)}
        disabled={regionsLoading || isSaving || regionChoices.length === 0}
      >
        {regionChoices.length === 0 && (
          <option value="">{regionsLoading ? 'Loading regions…' : 'No regions available'}</option>
        )}
        {!persRegion && regionChoices.length > 0 && (
          <option value="">Select a region</option>
        )}
        {regionChoices.map((region) => (
          <option key={region} value={region}>{region}</option>
        ))}
      </select>
    );

    const handleForceUnpair = async () => {
      if (!systemData?.system_id) return;
      try {
        await sendCommandMutation({ variables: { id: systemData.system_id } });
        setLoadingMessage('Force Unpair command sent.');
        // Start 5.5s countdown animation
        setUnpairCountdownKey((k) => k + 1);
        setShowUnpairCountdown(true);
        setTimeout(() => setShowUnpairCountdown(false), 5500);
      } catch (e) {
        console.error('Failed to send command', e);
        setLoadingMessage('Error sending Force Unpair command.');
      }
    };

    return (
      <div className="system-data">
        <h3>System Information</h3>
        
        {/* New System Status Card for read-only fields */}
        <div className="system-info-section system-status-card">
          <h4>System Status</h4>
          <div className="status-grid">
            <div className="status-item">
              <strong>Status: </strong>
              <span className={`status-indicator ${safeGet(systemData, 'basic_info.connection_state') === 'connected' ? 'online' : 'offline'}`}>
                {safeGet(systemData, 'basic_info.connection_state') === 'connected' ? 'Online' : 'Offline'}
              </span>
            </div>
            <div className="status-item">
              <strong>Uptime (24hr): </strong>
              <span>
                {safeGet(systemData, 'basic_info.system_uptime') !== 'N/A' 
                  ? `${Number(safeGet(systemData, 'basic_info.system_uptime')).toFixed(3)}%` 
                  : 'N/A'}
              </span>
            </div>
            <div className="status-item">
              <strong>RSRP (Signal Strength): </strong>
              <span>
                {safeGet(systemData, 'basic_info.mobile_network_status.signal_power') !== 'N/A' 
                  ? `${safeGet(systemData, 'basic_info.mobile_network_status.signal_power')} dBm` 
                  : 'N/A'}
              </span>
            </div>
            <div className="status-item">
              <strong>RSRQ (Signal Quality): </strong>
              <span>
                {safeGet(systemData, 'basic_info.mobile_network_status.signal_quality') !== 'N/A' 
                  ? `${safeGet(systemData, 'basic_info.mobile_network_status.signal_quality')} dB` 
                  : 'N/A'}
              </span>
            </div>
            <div className="status-item full-width">
              <strong>SIM ICCID: </strong>
              <span>{safeGet(systemData, 'basic_info.sim_iccid')}</span>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="system-info-section danger-zone" >
          <h4>Force Unpairing</h4>
          <div className="danger-actions">
            <button
              className="danger-button"
              onClick={handleForceUnpair}
              disabled={isForceUnpairDisabled || sendingCommand}
              title={isForceUnpairDisabled ? 'This command is only available on INACTIVE systems.' : 'Force unpair the next pendant transmission heard by this hub.'}
            >
              ⚠️ Force Unpair Pendant
            </button>
            {showUnpairCountdown && (
              <div className="countdown-container" key={unpairCountdownKey}>
                <div className="countdown-text">Press the pendant now to unpair</div>
                <div className="countdown-bar">
                  <div className="countdown-bar-inner" />
                </div>
              </div>
            )}
            <div className="danger-warning">
              Warning: If any pendant transmits near this hub in the next 5 seconds, it will be unpaired. Use with extreme caution, at a minimum of 1km distance from any active customer systems.
            </div>
          </div>
        </div>
        
        {/* New Editable Fields Card */}
        <div className="system-info-section editable-fields-card">
          <h4>System Configuration</h4>
          
          {/* Offline Warning */}
          {safeGet(systemData, 'basic_info.connection_state') !== 'connected' && (
            <div className="offline-warning">
              <strong>⚠️ System is offline.</strong> You can still configure the system, but note that <b>changes will not be visible on this page until the system is online to acknowledge them.</b>
            </div>
          )}
          
          <div className="editable-fields">
            <div className="form-group">
              <label htmlFor="nickname">Nickname:</label>
              <input
                type="text"
                id="nickname"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                className="form-control"
                placeholder="System nickname"
              />
              {/* Debug info */}
              <div className="debug-info">Previous value: {safeGet(systemData, 'basic_info.nickname', 'null')}</div>
            </div>
            <div className="form-group">
              <label htmlFor="customerReference">Customer Reference:</label>
              <input
                type="text"
                id="customerReference"
                value={customerReference}
                onChange={(e) => setCustomerReference(e.target.value)}
                className="form-control"
                placeholder="Customer reference"
              />
              {/* Debug info */}
              <div className="debug-info">Previous value: {safeGet(systemData, 'basic_info.customer_reference', 'null')}</div>
            </div>
            <div className="form-group">
              <label htmlFor="systemStatus">System Status:</label>
              <select
                id="systemStatus"
                value={systemStatus}
                onChange={(e) => setSystemStatus(e.target.value)}
                className="form-control"
              >
                <option value="INACTIVE">INACTIVE</option>
                <option value="READY_TO_ACTIVATE">READY_TO_ACTIVATE</option>
                <option value="ACTIVE">ACTIVE</option>
              </select>
              {/* Debug info */}
              <div className="debug-info">Previous value: {safeGet(systemData, 'basic_info.system_status.system_state', 'null')}</div>
            </div>
            <div className="form-group">
              <label htmlFor="persId">
                PERS ID:
                {systemStatus === 'ACTIVE' && <span className="lock-icon"> 🔒</span>}
              </label>
              <input
                type="text"
                id="persId"
                value={persId}
                onChange={(e) => setPersId(e.target.value)}
                className={`form-control ${systemStatus === 'ACTIVE' ? 'disabled-field' : ''}`}
                placeholder="PERS ID"
                disabled={systemStatus === 'ACTIVE'}
                title={systemStatus === 'ACTIVE' ? 'Cannot be modified while System Status is ACTIVE' : ''}
              />
              {/* Debug info */}
              <div className="debug-info">Previous value: {safeGet(systemData, 'configuration.pers_id', 'null')}</div>
            </div>
            {SHOW_TUNSTALL_PERS_REGION && isTunstallProvider && (
              <div className="form-group">
                <label htmlFor="persRegion">PERS Region:</label>
                {renderPersRegionSelect('persRegion')}
                {regionsLoading && (
                  <div className="debug-info">Loading Tunstall regions…</div>
                )}
                {regionsError && (
                  <div className="error-message">{regionsError}</div>
                )}
                <div className="debug-info">Previous value: {safeGet(systemData, 'advanced_info.organisation_setting.pers_region', 'null')}</div>
              </div>
            )}
            <div className="form-group">
              <label>
                Alert Endpoints:
                {systemStatus === 'ACTIVE' && <span className="lock-icon"> 🔒</span>}
              </label>
              {alertEndpoints.map((endpoint, index) => (
                <input
                  key={index}
                  type="text"
                  value={endpoint}
                  onChange={(e) => {
                    const newEndpoints = [...alertEndpoints];
                    newEndpoints[index] = e.target.value;
                    setAlertEndpoints(newEndpoints);
                  }}
                  className={`form-control endpoint-input ${systemStatus === 'ACTIVE' ? 'disabled-field' : ''}`}
                  placeholder={`Alert Endpoint ${index + 1}`}
                  disabled={systemStatus === 'ACTIVE'}
                  title={systemStatus === 'ACTIVE' ? 'Cannot be modified while System Status is ACTIVE' : ''}
                />
              ))}
              {/* Debug info */}
              <div className="debug-info">Previous endpoints: {systemData?.configuration?.alert_endpoints ? 
                systemData.configuration.alert_endpoints.map(ep => ep.address).join(', ') : 'null'}</div>
            </div>
            <div className="form-group">
              <div className="field-label">Features:</div>
              <label className="feature-toggle locked" title="Personal Alarm is always enabled.">
                <input type="checkbox" checked disabled readOnly />
                Personal Alarm
                <span className="lock-icon" aria-hidden="true">🔒</span>
              </label>
              <div className="debug-info">
                Previous value: {systemData.configuration?.features?.personal_alarm === true
                  ? 'Enabled'
                  : systemData.configuration?.features?.personal_alarm === false
                    ? 'Disabled'
                    : 'null'}
              </div>
              <label className="feature-toggle" title="Monitoring of smoke alarms using built-in microphones.">
                <input
                  type="checkbox"
                  checked={smokeAlarmDetection}
                  onChange={(e) => setSmokeAlarmDetection(e.target.checked)}
                />
                Smoke Alarm Detection
              </label>
              <div className="debug-info">
                Previous value: {systemData.configuration?.features?.smoke_alarm_detection === true
                  ? 'Enabled'
                  : systemData.configuration?.features?.smoke_alarm_detection === false
                    ? 'Disabled'
                    : 'null'}
              </div>
            </div>
            <div className="form-actions">
              <button
                className="save-button"
                onClick={async () => {
                  if (!systemData?.system_id) {
                    setSaveErrorMessage('No system loaded. Please enter a System ID and load system first.');
                    return;
                  }
                  setSaveErrorMessage('');
                  setIsSaving(true);
                  const errorDetails = [];
                  try {
                    // Persist nickname
                    await assignNicknameMutation({
                      variables: { system_id: systemData.system_id, nickname }
                    });
                  } catch (e) {
                    console.error('Failed to set Nickname', e);
                    errorDetails.push('Nickname');
                  }

                  try {
                    // Persist customer reference
                    await assignCustomerReferenceMutation({
                      variables: { system_id: systemData.system_id, reference: customerReference }
                    });
                  } catch (e) {
                    console.error('Failed to set Customer Reference', e);
                    errorDetails.push('Customer Reference');
                  }

                  try {
                    // Persist PERS ID
                    await setPersIdMutation({
                      variables: { system_id: systemData.system_id, pers_id: persId }
                    });
                  } catch (e) {
                    console.error('Failed to set PERS ID', e);
                    errorDetails.push('PERS ID');
                  }

                  try {
                    const provider = (systemData.advanced_info?.organisation_setting?.pers_provider || '').trim();
                    const previousRegion = (systemData.advanced_info?.organisation_setting?.pers_region || '').trim();
                    if (SHOW_TUNSTALL_PERS_REGION && provider === TUNSTALL_PROVIDER && persRegion && persRegion !== previousRegion) {
                      const selectedOrganisation = tunstallRegions.find((org) => org.pers_region === persRegion);
                      if (!selectedOrganisation) {
                        throw new Error(`No Tunstall organisation found for region ${persRegion}`);
                      }
                      await assignOrganisationMutation({
                        variables: {
                          system_id: systemData.system_id,
                          organisation_setting: toOrganisationSettingInput(selectedOrganisation),
                        },
                      });
                    }
                  } catch (e) {
                    console.error('Failed to assign PERS Region', e);
                    errorDetails.push('PERS Region');
                  }

                  try {
                    // Persist alert endpoints
                    const filtered = alertEndpoints
                      .map((address) => address.trim())
                      .filter((address) => address.length > 0)
                      .map((address) => ({ address, alert_protocol: 'CONTACT_ID_CSV' }));
                    await setAlertEndpointsMutation({
                      variables: { system_id: systemData.system_id, alert_endpoints: filtered }
                    });
                  } catch (e) {
                    console.error('Failed to set Alert Endpoints', e);
                    errorDetails.push('Alert Endpoints');
                  }

                  try {
                    // Persist system state
                    await changeSystemStateMutation({
                      variables: { system_id: systemData.system_id, state: systemStatus }
                    });
                  } catch (e) {
                    console.error('Failed to change System State', e);
                    errorDetails.push('System State');
                  }

                  try {
                    // Personal alarm stays enabled. Smoke alarm detection follows the toggle.
                    await setFeaturesMutation({
                      variables: {
                        system_id: systemData.system_id,
                        features: {
                          personal_alarm: true,
                          smoke_alarm_detection: smokeAlarmDetection,
                          client_requests: false,
                          service_reminders: false,
                        },
                      },
                    });
                  } catch (e) {
                    console.error('Failed to set features', e);
                    errorDetails.push('Smoke Alarm Detection');
                  }

                  // Refresh current system data regardless of outcome
                  try {
                    await fetchSystem({ variables: { system_id: systemData.system_id } });
                  } catch (e) {
                    console.warn('Failed to refresh system data after save', e);
                  }

                  if (errorDetails.length > 0) {
                    setLoadingMessage(`Saved with errors: ${errorDetails.join(', ')} could not be updated.`);
                    setSaveErrorMessage(`Some fields failed to save: ${errorDetails.join(', ')}. Please retry.`);
                  } else {
                    setLoadingMessage('Configuration updated successfully.');
                  }
                  setIsSaving(false);
                }}
                disabled={isSaving}
              >
                {isSaving ? 'Saving…' : 'Save Changes'}
              </button>
              {saveErrorMessage && (
                <div className="error-message" style={{ marginTop: '8px' }}>{saveErrorMessage}</div>
              )}
            </div>
          </div>
        </div>
        <div className="system-info-section">
          <h4>Basic Information</h4>
          <table className="system-info-table">
            <tbody>
              <tr>
                <td>System ID:</td>
                <td>{safeGet(systemData, 'system_id')}</td>
              </tr>
              <tr>
                <td>Nickname:</td>
                <td>{safeGet(systemData, 'basic_info.nickname')}</td>
              </tr>
              <tr>
                <td>Customer Reference:</td>
                <td>{safeGet(systemData, 'basic_info.customer_reference')}</td>
              </tr>
              <tr>
                <td>Connection State:</td>
                <td>{safeGet(systemData, 'basic_info.connection_state')}</td>
              </tr>
              <tr>
                <td>System Uptime (1 day):</td>
                <td>
                  {safeGet(systemData, 'basic_info.system_uptime') !== 'N/A' 
                    ? `${safeGet(systemData, 'basic_info.system_uptime')}%` 
                    : 'N/A'}
                </td>
              </tr>
              <tr>
                <td>SIM ICCID:</td>
                <td>{safeGet(systemData, 'basic_info.sim_iccid')}</td>
              </tr>
              <tr>
                <td>System State:</td>
                <td>{safeGet(systemData, 'basic_info.system_status.system_state')}</td>
              </tr>
              {systemData.basic_info?.mobile_network_status && (
                <>
                  <tr>
                    <td>Network Type:</td>
                    <td>{safeGet(systemData, 'basic_info.mobile_network_status.network_type')}</td>
                  </tr>
                  <tr>
                    <td>Signal Quality:</td>
                    <td>{safeGet(systemData, 'basic_info.mobile_network_status.signal_quality')}</td>
                  </tr>
                  <tr>
                    <td>Signal Power:</td>
                    <td>{safeGet(systemData, 'basic_info.mobile_network_status.signal_power')}</td>
                  </tr>
                </>
              )}
              {systemData.basic_info?.battery_state_of_charge && (
                <tr>
                  <td>Battery State:</td>
                  <td>{safeGet(systemData, 'basic_info.battery_state_of_charge.state_of_charge')}%</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {systemData.configuration && (
          <div className="system-info-section">
            <h4>Configuration</h4>
            <table className="system-info-table">
              <tbody>
                <tr>
                  <td>PERS ID:</td>
                  <td>{safeGet(systemData, 'configuration.pers_id')}</td>
                </tr>
                {systemData.configuration.alert_endpoints && (
                  <tr>
                    <td>Alert Endpoints:</td>
                    <td>
                      {Array.isArray(systemData.configuration.alert_endpoints) && systemData.configuration.alert_endpoints.length > 0 ? (
                        <ul>
                          {systemData.configuration.alert_endpoints.map((endpoint, index) => (
                            <li key={index}>
                              {endpoint.alert_protocol}: {endpoint.address}
                            </li>
                          ))}
                        </ul>
                      ) : 'None configured'}
                    </td>
                  </tr>
                )}
                <tr>
                  <td>Features:</td>
                  <td>
                    {systemData.configuration?.features ? (
                      <ul>
                        <li>Personal Alarm: {systemData.configuration.features.personal_alarm ? 'Enabled' : 'Disabled'}</li>
                        <li>Smoke Alarm Detection: {systemData.configuration.features.smoke_alarm_detection ? 'Enabled' : 'Disabled'}</li>
                      </ul>
                    ) : 'N/A'}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Only show desired configuration if it exists */}
            {systemData.configuration?.desired && (
              <>
                <h5>Desired Configuration</h5>
                <table className="system-info-table">
                  <tbody>
                    <tr>
                      <td>PERS ID:</td>
                      <td>{safeGet(systemData, 'configuration.desired.pers_id')}</td>
                    </tr>
                    {systemData.configuration.desired.alert_endpoints && (
                      <tr>
                        <td>Alert Endpoints:</td>
                        <td>
                          {Array.isArray(systemData.configuration.desired.alert_endpoints) && 
                           systemData.configuration.desired.alert_endpoints.length > 0 ? (
                            <ul>
                              {systemData.configuration.desired.alert_endpoints.map((endpoint, index) => (
                                <li key={index}>
                                  {endpoint.alert_protocol}: {endpoint.address}
                                </li>
                              ))}
                            </ul>
                          ) : 'None configured'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}

        {systemData.advanced_info?.organisation_setting && (
          <div className="system-info-section">
            <h4>Organization Settings</h4>
            <table className="system-info-table">
              <tbody>
                <tr>
                  <td>PERS Provider:</td>
                  <td>{safeGet(systemData, 'advanced_info.organisation_setting.pers_provider')}</td>
                </tr>
                <tr>
                  <td>PERS Region:</td>
                  <td>
                    {SHOW_TUNSTALL_PERS_REGION && isTunstallProvider ? (
                      <>
                        {renderPersRegionSelect('persRegionOrganisation')}
                        {regionsLoading && (
                          <div className="debug-info">Loading Tunstall regions…</div>
                        )}
                        {regionsError && (
                          <div className="error-message">{regionsError}</div>
                        )}
                        <div className="debug-info">Previous value: {safeGet(systemData, 'advanced_info.organisation_setting.pers_region', 'null')}</div>
                      </>
                    ) : (
                      safeGet(systemData, 'advanced_info.organisation_setting.pers_region')
                    )}
                  </td>
                </tr>
                <tr>
                  <td>Village Operator:</td>
                  <td>{safeGet(systemData, 'advanced_info.organisation_setting.village_operator')}</td>
                </tr>
                <tr>
                  <td>Village Name:</td>
                  <td>{safeGet(systemData, 'advanced_info.organisation_setting.village_name')}</td>
                </tr>
                <tr>
                  <td>Care Provider:</td>
                  <td>{safeGet(systemData, 'advanced_info.organisation_setting.care_provider')}</td>
                </tr>
                <tr>
                  <td>Care Provider Region:</td>
                  <td>{safeGet(systemData, 'advanced_info.organisation_setting.care_provider_region')}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Pendant Status Section - handle both array and single object formats */}
        {systemData.basic_info?.pendant_status && (
          <div className="system-info-section">
            <h4>Pendant Status</h4>
            <table className="system-info-table">
              <thead>
                <tr>
                  <th>Sensor ID</th>
                  <th>Connected</th>
                  <th>Zone</th>
                  <th>Last Heartbeat</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(systemData.basic_info.pendant_status) ? (
                  systemData.basic_info.pendant_status.length > 0 ? (
                    systemData.basic_info.pendant_status.map((pendant, index) => (
                      <tr key={index}>
                        <td>{pendant?.sensor_id || 'Unknown'}</td>
                        <td>{pendant?.connected ? 'Yes' : 'No'}</td>
                        <td>{pendant?.zone || 'N/A'}</td>
                        <td>{renderLocalTimeWithUtcTooltip(pendant?.last_heartbeat_utc)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4">No pendants found</td>
                    </tr>
                  )
                ) : (
                  <tr>
                    <td>{systemData.basic_info.pendant_status?.sensor_id || 'Unknown'}</td>
                    <td>{systemData.basic_info.pendant_status?.connected ? 'Yes' : 'No'}</td>
                    <td>{systemData.basic_info.pendant_status?.zone || 'N/A'}</td>
                    <td>{renderLocalTimeWithUtcTooltip(systemData.basic_info.pendant_status?.last_heartbeat_utc)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Alert Reports Section */}
        {hasItems(systemData, 'advanced_info.alert_reports.items') && (
          <div className="system-info-section">
            <h4>Recent Alerts</h4>
            <table className="system-info-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Timestamp</th>
                  <th>Resolution</th>
                  <th>Interface</th>
                </tr>
              </thead>
              <tbody>
                {systemData.advanced_info.alert_reports.items.map((alert, index) => (
                  <tr key={index}>
                    <td>{alert?.type || 'N/A'}</td>
                    <td>{renderLocalTimeWithUtcTooltip(alert?.timestamp_utc)}</td>
                    <td>{alert?.resolution || 'N/A'}</td>
                    <td>{alert?.interface || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Alert Events Section */}
        {hasItems(systemData, 'advanced_info.alerts.items') && (
          <div className="system-info-section">
            <h4>Recent Alert Events</h4>
            <table className="system-info-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Message</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {systemData.advanced_info.alerts.items.map((alert, index) => (
                  <tr key={index}>
                    <td>{alert?.type || 'N/A'}</td>
                    <td>{alert?.message || 'N/A'}</td>
                    <td>{alert?.priority || 'N/A'}</td>
                    <td>{alert?.status || 'N/A'}</td>
                    <td>{renderLocalTimeWithUtcTooltip(alert?.timestamp_utc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Firmware Versions Section */}
        {(systemData.configuration?.firmware_versions || systemData.advanced_info?.installed_versions) && (
          <div className="system-info-section">
            <h4>Firmware Versions</h4>
            <table className="system-info-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Configured Version</th>
                  <th>Installed Version</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Build a map of all services
                  const serviceMap = new Map();
                  
                  // Add configured versions
                  if (systemData.configuration?.firmware_versions) {
                    systemData.configuration.firmware_versions.forEach(fw => {
                      const serviceName = fw.service === 'frodo' ? 'service-manager' : fw.service;
                      serviceMap.set(serviceName, { 
                        configured: fw.version || 'N/A', 
                        installed: 'N/A' 
                      });
                    });
                  }
                  
                  // Add installed versions
                  if (systemData.advanced_info?.installed_versions) {
                    systemData.advanced_info.installed_versions.forEach(iv => {
                      const packageName = iv.package === 'frodo' ? 'service-manager' : iv.package;
                      if (serviceMap.has(packageName)) {
                        serviceMap.get(packageName).installed = iv.version || 'N/A';
                      } else {
                        serviceMap.set(packageName, { 
                          configured: 'N/A', 
                          installed: iv.version || 'N/A' 
                        });
                      }
                    });
                  }
                  
                  // Convert to array and sort
                  const services = Array.from(serviceMap.entries()).sort((a, b) => 
                    a[0].localeCompare(b[0])
                  );
                  
                  return services.map(([serviceName, versions], index) => (
                    <tr key={index}>
                      <td>{serviceName}</td>
                      <td>{versions.configured}</td>
                      <td>{versions.installed}</td>
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
        )}

        {/* Sensor Pairing Events Section */}
        {hasItems(systemData, 'advanced_info.sensor_pairing_events.items') && (
          <div className="system-info-section">
            <h4>Sensor Pairing Events</h4>
            <table className="system-info-table">
              <thead>
                <tr>
                  <th>Sensor ID</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {systemData.advanced_info.sensor_pairing_events.items.map((event, index) => (
                  <tr key={index}>
                    <td>{event?.sensor_id || 'N/A'}</td>
                    <td>{renderLocalTimeWithUtcTooltip(event?.timestamp_utc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Helper function to paginate through remaining results starting from a given nextToken
  const fetchRemainingPages = async (fetchFn, dataPath, system_id, days, limit, initialNextToken) => {
    let allItems = [];
    let nextToken = initialNextToken;
    
    while (nextToken) {
      const result = await fetchFn({ 
        variables: { system_id, days, limit, nextToken } 
      });
      
      // Navigate to the data using the path
      const data = dataPath.split('.').reduce((obj, key) => obj?.[key], result?.data?.getSystem?.advanced_info);
      
      if (data?.items) {
        allItems = [...allItems, ...data.items];
      }
      
      nextToken = data?.nextToken || null;
    }
    
    return allItems;
  };

  // Load timeline data with pagination
  const loadTimelineData = async () => {
    if (!systemData?.system_id) {
      setTimelineLoadingStatus('No system loaded. Please load a system first.');
      return;
    }

    setTimelineLoading(true);
    setTimelineData([]);
    setTimelineLoadingStatus('Loading timeline data...');

    const system_id = systemData.system_id;
    const days = 30;
    const limit = 100;

    try {
      // First, fetch all data types with initial query
      setTimelineLoadingStatus('Fetching initial data...');
      const initialResult = await fetchTimeline({
        variables: { system_id, days, limit }
      });

      const advancedInfo = initialResult?.data?.getSystem?.advanced_info;
      
      // Collect all timeline events
      let allEvents = [];

      // Process alerts
      setTimelineLoadingStatus('Processing alerts...');
      let alerts = advancedInfo?.alerts?.items || [];
      if (advancedInfo?.alerts?.nextToken) {
        const moreAlerts = await fetchRemainingPages(fetchAlertsPage, 'alerts', system_id, days, limit, advancedInfo.alerts.nextToken);
        alerts = [...alerts, ...moreAlerts];
      }
      alerts.forEach(item => {
        const detail = item.cid_code || item.message || 'No details';
        allEvents.push({
          timestamp_utc: item.timestamp_utc,
          type: 'Alert',
          event: `${item.type || 'Unknown'} - ${item.status || 'N/A'} - ${detail}`
        });
      });

      // Process alert reports
      setTimelineLoadingStatus('Processing alert reports...');
      let alertReports = advancedInfo?.alert_reports?.items || [];
      if (advancedInfo?.alert_reports?.nextToken) {
        const moreReports = await fetchRemainingPages(fetchAlertReportsPage, 'alert_reports', system_id, days, limit, advancedInfo.alert_reports.nextToken);
        alertReports = [...alertReports, ...moreReports];
      }
      alertReports.forEach(item => {
        const parts = [];
        if (item.type) parts.push(`Type: ${item.type}`);
        if (item.resolution) parts.push(`Resolution: ${item.resolution}`);
        if (item.interface) parts.push(`Interface: ${item.interface}`);
        if (item.endpoint) parts.push(`Endpoint: ${item.endpoint}`);
        if (item.callback_wait_time != null) parts.push(`Wait: ${item.callback_wait_time}s`);
        if (item.callback_duration != null) parts.push(`Duration: ${item.callback_duration}s`);
        allEvents.push({
          timestamp_utc: item.timestamp_utc,
          type: 'Alert Report',
          event: parts.join(', ') || 'No data'
        });
      });

      // Process shutdown notifications
      setTimelineLoadingStatus('Processing shutdown events...');
      let shutdowns = advancedInfo?.shutdown_notifications?.items || [];
      if (advancedInfo?.shutdown_notifications?.nextToken) {
        const moreShutdowns = await fetchRemainingPages(fetchShutdownPage, 'shutdown_notifications', system_id, days, limit, advancedInfo.shutdown_notifications.nextToken);
        shutdowns = [...shutdowns, ...moreShutdowns];
      }
      shutdowns.forEach(item => {
        allEvents.push({
          timestamp_utc: item.timestamp_utc,
          type: 'Shutdown',
          event: 'System shutdown'
        });
      });

      // Process boot notifications
      setTimelineLoadingStatus('Processing boot events...');
      let boots = advancedInfo?.boot_notifications?.items || [];
      if (advancedInfo?.boot_notifications?.nextToken) {
        const moreBoots = await fetchRemainingPages(fetchBootPage, 'boot_notifications', system_id, days, limit, advancedInfo.boot_notifications.nextToken);
        boots = [...boots, ...moreBoots];
      }
      boots.forEach(item => {
        allEvents.push({
          timestamp_utc: item.timestamp_utc,
          type: 'Boot',
          event: 'System boot'
        });
      });

      // Process network events
      setTimelineLoadingStatus('Processing network events...');
      let networkEvents = advancedInfo?.mobile_network_detailed_events?.items || [];
      if (advancedInfo?.mobile_network_detailed_events?.nextToken) {
        const moreNetwork = await fetchRemainingPages(fetchNetworkPage, 'mobile_network_detailed_events', system_id, days, limit, advancedInfo.mobile_network_detailed_events.nextToken);
        networkEvents = [...networkEvents, ...moreNetwork];
      }
      networkEvents.forEach(item => {
        const parts = [];
        const errorValues = [255, -255, 32768, -32768];
        const formatValue = (val) => errorValues.includes(val) ? 'ERROR' : val;
        
        // Signal info
        if (item.signalInfo?.signalPower != null) parts.push(`RSRP: ${formatValue(item.signalInfo.signalPower)}`);
        if (item.signalInfo?.signalQuality != null) parts.push(`RSRQ: ${formatValue(item.signalInfo.signalQuality)}`);
        // Network info
        if (item.networkInfo?.imsRegistration) parts.push(`IMS: ${item.networkInfo.imsRegistration}`);
        if (item.networkInfo?.cid) parts.push(`CID: ${item.networkInfo.cid}`);
        if (item.networkInfo?.activeBand != null) parts.push(`Band: ${formatValue(item.networkInfo.activeBand)}`);
        if (item.networkInfo?.plmn) parts.push(`PLMN: ${item.networkInfo.plmn}`);
        if (item.networkInfo?.tac) parts.push(`TAC: ${item.networkInfo.tac}`);
        
        allEvents.push({
          timestamp_utc: item.timestamp_utc,
          type: 'Network',
          event: parts.join(', ') || 'No data'
        });
      });

      // Process connectivity events
      setTimelineLoadingStatus('Processing connectivity events...');
      let connectivityEvents = advancedInfo?.connectivity_events?.items || [];
      if (advancedInfo?.connectivity_events?.nextToken) {
        const moreConnectivity = await fetchRemainingPages(fetchConnectivityPage, 'connectivity_events', system_id, days, limit, advancedInfo.connectivity_events.nextToken);
        connectivityEvents = [...connectivityEvents, ...moreConnectivity];
      }
      connectivityEvents.forEach(item => {
        const state = item.connection_state?.toLowerCase();
        const event = state === 'connected' ? 'Connected to cloud' : 
                      state === 'disconnected' ? 'Disconnected from cloud' : 
                      `State: ${item.connection_state || 'Unknown'}`;
        allEvents.push({
          timestamp_utc: item.timestamp_utc,
          type: 'Connectivity',
          event
        });
      });

      // Process battery events
      setTimelineLoadingStatus('Processing battery events...');
      let batteryEvents = advancedInfo?.battery_soc_events?.items || [];
      if (advancedInfo?.battery_soc_events?.nextToken) {
        const moreBattery = await fetchRemainingPages(fetchBatteryPage, 'battery_soc_events', system_id, days, limit, advancedInfo.battery_soc_events.nextToken);
        batteryEvents = [...batteryEvents, ...moreBattery];
      }
      batteryEvents.forEach(item => {
        const parts = [];
        if (item.state_of_charge != null) parts.push(`SoC: ${item.state_of_charge.toFixed(1)}%`);
        if (item.dc_input_voltage != null) parts.push(`DC Input: ${(item.dc_input_voltage / 1000).toFixed(3)}V`);
        allEvents.push({
          timestamp_utc: item.timestamp_utc,
          type: 'Battery',
          event: parts.join(', ') || 'No data'
        });
      });

      // Process pendant events
      setTimelineLoadingStatus('Processing pendant events...');
      let pendantEvents = advancedInfo?.pendant_events?.items || [];
      if (advancedInfo?.pendant_events?.nextToken) {
        const morePendant = await fetchRemainingPages(fetchPendantPage, 'pendant_events', system_id, days, limit, advancedInfo.pendant_events.nextToken);
        pendantEvents = [...pendantEvents, ...morePendant];
      }
      pendantEvents.forEach(item => {
        const pendants = item.pendant_status || [];
        if (pendants.length === 0) {
          allEvents.push({
            timestamp_utc: item.timestamp_utc,
            type: 'Pendant Status',
            event: 'No pendant data'
          });
        } else {
          const pendantLines = pendants.map(p => {
            const id = p.sensor_id || 'Unknown';
            const zone = p.zone != null ? `Zone ${p.zone}` : 'Zone N/A';
            const connected = p.connected ? 'Connected' : 'Disconnected';
            const lastHeartbeat = p.last_heartbeat_utc 
              ? new Date(p.last_heartbeat_utc).toLocaleString() 
              : 'N/A';
            return `ID: ${id}, ${zone}, ${connected}, Last HB: ${lastHeartbeat}`;
          });
          allEvents.push({
            timestamp_utc: item.timestamp_utc,
            type: 'Pendant Status',
            event: pendantLines.join('\n')
          });
        }
      });

      // Process sensor pairing events
      setTimelineLoadingStatus('Processing pairing events...');
      let pairingEvents = advancedInfo?.sensor_pairing_events?.items || [];
      if (advancedInfo?.sensor_pairing_events?.nextToken) {
        const morePairing = await fetchRemainingPages(fetchPairingPage, 'sensor_pairing_events', system_id, days, limit, advancedInfo.sensor_pairing_events.nextToken);
        pairingEvents = [...pairingEvents, ...morePairing];
      }
      pairingEvents.forEach(item => {
        allEvents.push({
          timestamp_utc: item.timestamp_utc,
          type: 'Sensor Pairing',
          event: `Sensor paired: ${item.sensor_id || 'Unknown'}`
        });
      });

      // Sort all events by timestamp (newest first)
      allEvents.sort((a, b) => {
        const dateA = new Date(a.timestamp_utc || 0);
        const dateB = new Date(b.timestamp_utc || 0);
        return dateB - dateA;
      });

      setTimelineData(allEvents);
      setTimelineLoadingStatus(`Loaded ${allEvents.length} events`);
    } catch (error) {
      console.error('Error loading timeline:', error);
      setTimelineLoadingStatus('Error loading timeline data. Please try again.');
    } finally {
      setTimelineLoading(false);
    }
  };

  // Render the timeline section
  const renderTimelineSection = () => {
    if (!systemData) return null;

    return (
      <div className="system-info-section">
        <h4>Detailed Timeline</h4>
        <div className="timeline-controls">
          <button
            className="load-system-button"
            onClick={loadTimelineData}
            disabled={timelineLoading}
          >
            {timelineLoading ? 'Loading...' : 'Load Timeline'}
          </button>
          {timelineLoadingStatus && (
            <span className="timeline-status">{timelineLoadingStatus}</span>
          )}
        </div>
        
        {timelineData.length > 0 && (
          <table className="system-info-table timeline-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Type</th>
                <th>Event</th>
              </tr>
            </thead>
            <tbody>
              {timelineData.map((event, index) => (
                <tr key={index}>
                  <td>{renderLocalTimeWithUtcTooltip(event.timestamp_utc)}</td>
                  <td>{event.type}</td>
                  <td>{event.event}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        
        {!timelineLoading && timelineData.length === 0 && timelineLoadingStatus && !timelineLoadingStatus.includes('Loading') && (
          <p className="timeline-empty">Click "Load Timeline" to fetch detailed event history (last 30 days).</p>
        )}
      </div>
    );
  };

  return (
    <div className="device-list-page">
      <Helmet>
        <title>System Setup</title>
      </Helmet>
      
      <div className="system-setup-container">
        <div className="system-id-input-container">
          <input
            type="text"
            value={systemId}
            onChange={handleSystemIdChange}
            placeholder="Enter System ID"
            className="system-id-input"
          />
          <button 
            onClick={handleLoadSystem}
            className="load-system-button"
            disabled={loading}
          >
            Load
          </button>
        </div>
        
        {loadingMessage && (
          <div className="loading-message">{loadingMessage}</div>
        )}
        
        {renderSystemData()}
        {renderTimelineSection()}
      </div>
    </div>
  );
};

export default SystemSetupPage;
